/**
 * The kinship finder (`/kinship`) — docs/08-frontend-and-ux.md §3.7 and §5,
 * over `GET /kinship/path` (docs/05 §7.1).
 *
 * The pair lives in the URL (`?from=&to=`), so every answer this screen gives is
 * a link someone can send to the cousin it is about — which is most of the point
 * of having the screen at all.
 *
 * Two deliberate choices about being wrong gracefully:
 *
 *  - **"No relation found" is a result, not an error** (§5.5). It renders as an
 *    answer, in the normal card, with the caveat that the search is bounded.
 *  - **Ambiguity is shown, not resolved** (§5.4). Endogamy and remarriage mean a
 *    pair can be related several ways; `mode=all` returns them ranked, and the
 *    header says how many there are rather than quietly picking one.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { KinshipPathView, PersonDetail, PersonSummary } from '@famtree/shared';
import { kinshipApi, personsApi } from '../../api/client.js';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../app/session.js';
import { PersonPicker } from '../people/PersonPicker.js';
import { RecordRelationship } from './RecordRelationship.js';
import { personName, toPersonSummary } from '../people/labels.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './kinship.module.css';

export function KinshipFinderPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();

  const fromId = params.get('from') ?? '';
  const toId = params.get('to') ?? '';

  const [from, setFrom] = useState<PersonSummary | null>(null);
  const [to, setTo] = useState<PersonSummary | null>(null);
  const queryClient = useQueryClient();
  const canWrite = user?.role === 'admin' || user?.can_propose === true;

  // The URL is the source of truth, so a shared link resolves its own people
  // rather than showing two empty pickers next to a populated answer.
  const fromDetail = usePersonDetail(fromId);
  const toDetail = usePersonDetail(toId);
  const fromPerson = fromDetail ? toPersonSummary(fromDetail.person) : null;
  const toPerson = toDetail ? toPersonSummary(toDetail.person) : null;

  useEffect(() => {
    if (fromPerson && fromPerson.id !== from?.id) setFrom(fromPerson);
    if (toPerson && toPerson.id !== to?.id) setTo(toPerson);
  }, [fromPerson, toPerson, from?.id, to?.id]);

  const setPair = (nextFrom: string | null, nextTo: string | null): void => {
    const next = new URLSearchParams();
    if (nextFrom) next.set('from', nextFrom);
    if (nextTo) next.set('to', nextTo);
    setParams(next, { replace: false });
  };

  const samePerson = fromId !== '' && fromId === toId;

  const result = useQuery({
    queryKey: ['kinship-path', fromId, toId],
    queryFn: () => kinshipApi.path(fromId, toId, 'all'),
    enabled: fromId !== '' && toId !== '' && !samePerson,
  });

  return (
    <div>
      <h1>{t('kinship.title')}</h1>
      <p className={ui.muted}>{t('kinship.intro')}</p>

      <div className={styles.pickers}>
        <PersonPicker
          label={t('kinship.from')}
          value={from}
          onChange={(person) => {
            setFrom(person);
            setPair(person?.id ?? null, toId || null);
          }}
          excludeId={toId || undefined}
        />
        <button
          type="button"
          className={ui.buttonSecondary}
          onClick={() => {
            setFrom(to);
            setTo(from);
            setPair(toId || null, fromId || null);
          }}
          aria-label={t('kinship.swap')}
        >
          ⇄
        </button>
        <PersonPicker
          label={t('kinship.to')}
          value={to}
          onChange={(person) => {
            setTo(person);
            setPair(fromId || null, person?.id ?? null);
          }}
          excludeId={fromId || undefined}
        />
      </div>

      {user?.person_id && (
        <div className={ui.actions ?? ''}>
          <button
            type="button"
            className={ui.buttonSecondary}
            onClick={() => setPair(user.person_id!, toId || null)}
          >
            {t('kinship.useMe')}
          </button>
        </div>
      )}

      {samePerson && <p className={ui.notice}>{t('kinship.samePerson')}</p>}

      {result.isPending && fromId && toId && !samePerson && (
        <p className={ui.muted}>{t('app.loading')}</p>
      )}
      {result.isError && <p className={ui.problem}>{problemMessage(result.error, t)}</p>}

      {result.isSuccess && (
        <Answer
          paths={result.data.paths}
          related={result.data.related}
          flags={result.data.flags}
          truncated={result.data.truncated}
        />
      )}

      {/* The moment this is most wanted is the moment the search comes back
          empty: the family knows the answer, the tree does not yet. */}
      {result.isSuccess && from && to && canWrite && (
        <RecordRelationship
          from={from}
          to={to}
          fromParents={fromDetail?.relations.parents.map((row) => row.person) ?? []}
          toParents={toDetail?.relations.parents.map((row) => row.person) ?? []}
          onRecorded={() => {
            void queryClient.invalidateQueries({ queryKey: ['kinship-path'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * The full profile behind an id in the URL.
 *
 * The finder needs two things from it: a summary for the pickers, and the
 * recorded parents, which is what lets `RecordRelationship` reuse a parent
 * already in the tree instead of inventing one beside it.
 */
function usePersonDetail(id: string): PersonDetail | null {
  const query = useQuery({
    queryKey: ['person', id],
    queryFn: () => personsApi.get(id),
    enabled: id !== '',
  });
  return query.data ?? null;
}

function Answer({
  paths,
  related,
  flags,
  truncated,
}: {
  paths: KinshipPathView[];
  related: boolean;
  flags: string[];
  truncated: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  if (!related) {
    return (
      <div className={styles.pathCard} role="status">
        <p className={styles.sentence}>{t('kinship.noRelation')}</p>
      </div>
    );
  }

  // Paths that say the same thing are one answer with several witnesses (§5.4),
  // not several answers — showing them as duplicates reads like a bug.
  const groups = new Map<string, KinshipPathView[]>();
  for (const path of paths) {
    groups.set(path.label, [...(groups.get(path.label) ?? []), path]);
  }
  const ordered = [...groups.entries()];

  return (
    <div>
      <p className={ui.muted}>{t('kinship.relatedWays', { count: ordered.length })}</p>
      {flags.includes('consanguineous') && (
        <p className={ui.notice}>{t('kinship.consanguineous')}</p>
      )}
      {flags.includes('dates_unknown') && <p className={ui.muted}>{t('kinship.datesUnknown')}</p>}

      {ordered.map(([label, witnesses], index) => (
        <section
          key={label}
          className={index === 0 ? styles.pathCardBest : styles.pathCard}
          aria-label={label}
        >
          <h2 className={styles.groupTitle}>
            {index === 0 ? t('kinship.closest') : t('kinship.alsoRelated')}
          </h2>
          <p className={styles.sentence}>{witnesses[0]!.sentence}</p>
          {witnesses.map((path) => (
            <PathStrip key={path.rank} path={path} />
          ))}
        </section>
      ))}

      {truncated && <p className={ui.muted}>{t('kinship.truncated')}</p>}
    </div>
  );
}

/** The chain of people behind one answer, with the edge that joins each pair. */
function PathStrip({ path }: { path: KinshipPathView }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <div className={styles.strip}>
        {path.steps.map((step, index) => (
          <span key={`${step.person.id}-${String(index)}`} className={styles.strip}>
            {step.edge && (
              <span className={styles.stripEdge}>
                — {step.edge_label ?? t(`kinship.edges.${step.edge}`)} →
              </span>
            )}
            <Link className={styles.stripPerson} to={`/people/${step.person.id}`}>
              {personName(step.person)}
            </Link>
          </span>
        ))}
        <span className={styles.spacer} />
        <span className={styles.chip}>{t('kinship.steps', { count: path.degree })}</span>
      </div>
    </div>
  );
}
